import {DOCUMENT, inject, Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {defer, from, Observable, switchMap, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';

import {environment} from '../../environments/environment';
import {TokenRefreshService} from '../services/auth/token-refresh.service';

/**
 * Endpunkte, die die Sitzung selbst verwalten: Sie setzen oder lesen das Refresh-Cookie, brauchen
 * kein Access-Token und dürfen nach einem 401 nicht refreshen – sonst refresht ein 401 von
 * `/auth/refresh` sich selbst, und ein falsches Passwort beim Login würde zum Refresh.
 */
const SESSION_PATHS = ['/auth/login', '/auth/verify2FA', '/auth/refresh', '/auth/logout', '/auth/reactivateAccount'];

/**
 * Angemeldete Endpunkte, nach denen das Backend alle anderen Sitzungen widerruft und für die
 * aktuelle ein neues Refresh-Cookie setzt. Ohne Credentials verwürfe der Browser dieses Cookie.
 */
const SESSION_RENEWING_PATHS = ['/auth/renewPassword', '/auth/activate2FA', '/auth/deactivate2FA'];

/** Kein Refresh nach 401: hier heißt 401 „falsches Passwort“, nicht „Token abgelaufen“. */
const NO_RETRY_PATHS = [...SESSION_PATHS, '/auth/renewPassword'];

const STANDARD_PORTS = ['80', '443'];

/**
 * Hängt das Access-Token an und erneuert es bei Bedarf.
 *
 * <p>Ersetzt den Interceptor von `@auth0/angular-jwt` (`JwtModule.forRoot`) und übernimmt dessen
 * Domainregel: das Token geht nur an den eigenen Origin und an die Hosts aus
 * `environment.allowedDomains` (Vergleich auf `host[:port]`, Standardports weggelassen).
 *
 * <ul>
 *   <li>Vor dem Request holt er über {@link TokenRefreshService#getValidToken} ein Token, das noch
 *       mindestens eine Minute gilt. Ein vorhandener `Authorization`-Header wird überschrieben –
 *       einige Komponenten bauen ihn von Hand aus dem localStorage und hätten sonst ein altes
 *       Token.</li>
 *   <li>Antwortet das Backend trotzdem mit 401, wird einmal refresht und der Request wiederholt.
 *       Ein zweites 401 geht an den Aufrufer.</li>
 * </ul>
 *
 * <p>Muss als ERSTER Interceptor registriert sein, vor dem {@link RequestDedupInterceptor}: Dann
 * wiederholt jeder Aufrufer seinen Request selbst, und zusammengefasste Anfragen tragen bereits
 * das frische Token.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly tokenRefresh = inject(TokenRefreshService);
  private readonly document = inject(DOCUMENT);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const url = new URL(request.url, this.document.location.origin);
    if (!this.isAllowedDomain(url)) {
      return next.handle(request);
    }

    const path = url.pathname;
    if (matches(path, SESSION_PATHS)) {
      return next.handle(request.clone({withCredentials: true}));
    }

    const withCredentials = matches(path, SESSION_RENEWING_PATHS);
    const retry = !matches(path, NO_RETRY_PATHS);

    return defer(() => from(this.tokenRefresh.getValidToken())).pipe(
      switchMap(token => next.handle(this.authorize(request, token, withCredentials)).pipe(
        catchError((error: unknown) => {
          if (!retry || !token || !(error instanceof HttpErrorResponse) || error.status !== 401) {
            return throwError(() => error);
          }
          return from(this.tokenRefresh.refreshAfterUnauthorized(token)).pipe(
            switchMap(fresh => fresh
              ? next.handle(this.authorize(request, fresh, withCredentials))
              : throwError(() => error))
          );
        })
      ))
    );
  }

  private authorize(request: HttpRequest<unknown>, token: string | null,
                    withCredentials: boolean): HttpRequest<unknown> {
    if (!token) {
      return withCredentials ? request.clone({withCredentials}) : request;
    }
    return request.clone({
      setHeaders: {Authorization: `Bearer ${token}`},
      ...(withCredentials ? {withCredentials} : {})
    });
  }

  private isAllowedDomain(url: URL): boolean {
    if (url.host === this.document.location.host) {
      return true;
    }
    const host = url.port && !STANDARD_PORTS.includes(url.port) ? `${url.hostname}:${url.port}` : url.hostname;
    return environment.allowedDomains.includes(host);
  }
}

function matches(path: string, paths: string[]): boolean {
  return paths.some(candidate => path.endsWith(candidate));
}
